import React, {
    ForwardedRef,
    MutableRefObject,
    RefObject,
    forwardRef,
    useEffect,
    useRef,
    useState,
    useCallback,
} from "react";

import { ERRORED_CLASSNAME } from "@impero-com/common/utils/form/erroredElements";
import { t } from "@impero-com/common/utils/i18n";
import { mergeRefs } from "@impero-com/common/utils/mergeRefs";
import { FormField, FormLabel, renderMarkdown } from "@impero-com/ui";
import { Typography } from "antd/es";
import { cx } from "class-variance-authority";
import { RefCallBack } from "react-hook-form";

import { MarkdownEditor } from "components/markdownEditor";
import {
    MarkdownEditorBlurEvent,
    MarkdownEditorCustomButtonSpec,
    MarkdownEditorFocusOptions,
    MarkdownEditorMethods,
    MarkdownSelectionRange,
    SourceToggleMode,
} from "components/markdownEditor/types";

import * as theme from "./theme.css";

export interface MarkdownInputBaseProps {
    label?: React.ReactNode;
    disabled?: boolean;
    // The text rendered when the atom is holding an empty string
    placeholder?: string;
    // normal (default): A regular input, with borders, gray-background when disabled
    // borderless: An input without borders, unless focused
    styleVariant?: "normal" | "borderless";
    // Gives focus to this component when mounted
    autoFocus?: boolean;
}

export interface MarkdownInputProps extends MarkdownInputBaseProps {
    /**
     * Markdown to display.
     * The primary reason why it's optional is to make `getFieldDecorator()` in `CreateTextArea` compile.
     */
    value?: string;
    error?: string | undefined;
    onChange?: (value: string) => void;
    onBlur?: (
        event: React.FocusEvent<HTMLTextAreaElement> | MarkdownEditorBlurEvent
    ) => void;
    fieldRef?: RefCallBack;
    onFocus?: () => void;
    sourceToggleMode?: SourceToggleMode;
    customButtonSpecs?: Array<MarkdownEditorCustomButtonSpec>;
    headerAction?: React.ReactNode;
    onSelect?: (selection?: MarkdownSelectionRange) => void;
    // When true, keeps the editor open even when it loses focus
    keepEditorOpen?: boolean;
}

/**
 * Basic form component based on ant design's TextArea
 *
 * Renders markdown content when unfocused and without error
 *
 * This component leverages the following constraints from the validator:
 *  - `min(n)` is mapped to `minLength={n}`
 *  - `max(n)` is mapped to `maxLength={n}`
 *  - `required` is mapped to `required`
 */
export const MarkdownInput = forwardRef(
    (
        {
            label,
            placeholder,
            error,
            onChange,
            onBlur,
            fieldRef,
            value = "",
            disabled = false,
            styleVariant = "normal",
            autoFocus = false,
            sourceToggleMode,
            onFocus,
            customButtonSpecs,
            headerAction,
            onSelect,
            keepEditorOpen,
        }: MarkdownInputProps,
        forwardedRef: ForwardedRef<MarkdownEditorMethods>
    ) => {
        const [editing, setEditing] = useState(autoFocus);
        const markdownEditorRef: RefObject<MarkdownEditorMethods | null> =
            useRef(null);
        const inputRef: RefObject<MarkdownEditorMethods | null> =
            forwardedRef === null
                ? markdownEditorRef
                : (forwardedRef as MutableRefObject<MarkdownEditorMethods>);

        const displayTextArea =
            editing || keepEditorOpen || error !== undefined;
        const prevKeepEditorOpen = useRef(keepEditorOpen);

        const markdownViewClasses = cx(theme.markdownView, {
            [theme.disabled]: disabled,
            [theme.borderless]: styleVariant === "borderless",
            [theme.placeholderShown]: !isValuePresent(value),
        });

        const handleBlur = useCallback(
            (
                event:
                    | React.FocusEvent<HTMLTextAreaElement>
                    | MarkdownEditorBlurEvent
            ) => {
                if (keepEditorOpen) {
                    return;
                }
                setEditing(false);
                onBlur?.(event);
                onSelect?.(undefined);
            },
            [keepEditorOpen, onBlur, onSelect]
        );

        /**
         * If the keepOpen was previously true, we want to handleBlur when it becomes false
         */
        useEffect(() => {
            if (prevKeepEditorOpen.current && !keepEditorOpen) {
                handleBlur({} as MarkdownEditorBlurEvent);
            }
            prevKeepEditorOpen.current = keepEditorOpen;
        }, [keepEditorOpen, onBlur, onSelect, handleBlur]);

        const handleFocus = () => {
            setEditing(true);
            onFocus?.();
        };

        const effectivePlaceholder =
            placeholder ??
            (styleVariant === "borderless"
                ? t("Form_No_Field_Set")
                : undefined);

        // After clicking on text, place cursor at end (is at start otherwise)
        useEffect(() => {
            window.setTimeout(() => {
                // HACK: By some reason, because MarkdownEditor is lazy loaded,
                // `this.markdownEditorRef.current` can be still `null` in the moment, so we need to wait
                // a bit before focusing.
                if (markdownEditorRef.current) {
                    markdownEditorRef.current.focus(undefined, {
                        defaultSelection: "rootEnd",
                    });
                }
            }, 50);
        }, [displayTextArea, inputRef]);

        useEffect(() => {
            // Preload lazy MarkdownEditor component
            MarkdownEditor.preload();
        }, []);

        const renderInput = () => {
            let autoFocus: boolean | MarkdownEditorFocusOptions = false;
            if (!error) {
                autoFocus = { defaultSelection: "rootEnd" };
            }
            return (
                <MarkdownEditor
                    ref={mergeRefs(inputRef, fieldRef)}
                    initialContent={value}
                    disabled={disabled}
                    autoFocus={autoFocus}
                    onChange={onChange}
                    placeholder={placeholder}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    sourceToggleMode={sourceToggleMode}
                    status={error !== undefined ? "error" : "normal"}
                    customButtonSpecs={customButtonSpecs}
                    onSelect={onSelect}
                />
            );
        };

        return (
            <FormField
                error={error}
                className={cx(
                    error !== undefined ? ERRORED_CLASSNAME : undefined
                )}
                lite={label === undefined}
            >
                <div className={theme.markdownLabelContainer}>
                    {label && (
                        <FormLabel
                            disabled={disabled}
                            showPencil={styleVariant === "borderless"}
                            onClick={() => setEditing(true)}
                        >
                            {label}
                        </FormLabel>
                    )}
                    {headerAction}
                </div>
                {displayTextArea ? (
                    renderInput()
                ) : (
                    <div
                        tabIndex={!disabled ? 0 : undefined}
                        onFocus={!disabled ? () => setEditing(true) : undefined}
                        role="textbox"
                        className={theme.whiteBackground}
                    >
                        <Typography.Text className={markdownViewClasses}>
                            {isValuePresent(value)
                                ? renderMarkdown(value)
                                : effectivePlaceholder}
                        </Typography.Text>
                    </div>
                )}
            </FormField>
        );
    }
);

function isValuePresent(value: string | undefined): boolean {
    return value !== undefined && value.length > 0;
}
